import React, { useState, useEffect, useRef } from "react";
import classes from "./UploadExcelModal2.module.css"; 
import { X, Download, Upload, CheckCircle } from "lucide-react";
import Swal from "sweetalert2";
import { Spinner } from "react-bootstrap";
import api from "../../../Components/API/Api";

const UploadExcelModal2 = ({ isOpen, onClose, companyId, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [file, setFile] = useState(null);
  
  // Form Fields
  const [savingsType, setSavingsType] = useState("");
  const [currency, setCurrency] = useState("");
  const [date, setDate] = useState("");

  // Dropdown Data
  const [savingsProducts, setSavingsProducts] = useState([]); 
  const [currencies, setCurrencies] = useState([]);
  
  // Loading States
  const [loadingSavingsProducts, setLoadingSavingsProducts] = useState(false); 
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const fileInputRef = useRef(null);

  // Reset & Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setFile(null);
      setSavingsType("");
      setCurrency("");
      setDate("");
      setSavingsProducts([]);
      fetchAllDropdownData();
    }
  }, [isOpen]);

  // --- Fetch dropdown data (Savings Products and Currencies) ---
  const fetchAllDropdownData = async () => {
    if (!companyId) return;
    setLoadingSavingsProducts(true);
    setLoadingCurrencies(true);
    try {
      // 1. Fetch Savings Products (Now independent of Member)
      const productsRes = await api.get('/v1/savings_product', {
        params: { company_id: companyId }
      });
      // Handle potential API response structures
      let productsData = [];
      if (productsRes?.data?.data && Array.isArray(productsRes.data.data)) {
        productsData = productsRes.data.data;
      } else if (Array.isArray(productsRes?.data)) {
        productsData = productsRes.data;
      }
      setSavingsProducts(productsData);

      // 2. Fetch Currencies
      const currencyRes = await api.get('/v1/fetch-all-currencies', {
        params: { company_id: companyId }
      });
      
      let currenciesData = [];
      if (currencyRes?.data?.data && Array.isArray(currencyRes.data.data)) {
        currenciesData = currencyRes.data.data;
      } else if (Array.isArray(currencyRes?.data)) {
        currenciesData = currencyRes.data;
      }
      setCurrencies(currenciesData);

    } catch (error) {
      console.error("❌ Error fetching dropdowns", error);
      setSavingsProducts([]);
      setCurrencies([]);
    } finally {
      setLoadingSavingsProducts(false);
      setLoadingCurrencies(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      const response = await api.get('v1/download_saving_template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'savings_data_template.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setCurrentStep(2);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: error.response?.data?.message || 'Could not download the template.'
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleSubmit = async () => {
    if (!file) {
      Swal.fire({ icon: 'error', title: 'Missing File', text: 'Please upload the completed Excel file.' });
      return;
    }
    if (!savingsType) {
      Swal.fire({ icon: 'error', title: 'Missing Field', text: 'Please select a Savings Type.' });
      return;
    }
    if (!currency) {
      Swal.fire({ icon: 'error', title: 'Missing Field', text: 'Please select a Currency.' });
      return;
    }
    if (!date) {
      Swal.fire({ icon: 'error', title: 'Missing Field', text: 'Please select a Date.' });
      return;
    }

    setIsSaving(true);
    try {
      const formData = new FormData();
      
      formData.append('company_id', companyId);
      formData.append('file', file);
      formData.append('savings_type', savingsType);
      formData.append('currency', currency);
      formData.append('transaction_date', date);

      const res = await api.post('/account/upload-member-savings', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.status === 200 || res.status === 201) {
        Swal.fire({
          icon: 'success',
          title: 'Upload Successful',
          text: res.data?.message || 'Member savings uploaded successfully.'
        });

        if (onSuccess) onSuccess();
        onClose(); 
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: error.response?.data?.message || 'Failed to upload the file.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={classes.modalOverlay} onClick={onClose}>
      <div className={classes.customModal} onClick={(e) => e.stopPropagation()}>
        <div className={classes.modalContent}>
          <div className={classes.modalHeader}>
            <div>
              <h5 className={classes.modalTitle}>Upload Savings Account</h5>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0' }}>
                Three steps to upload your savings account
              </p>
            </div>
            <button type="button" className={classes.modalCloseBtn} onClick={onClose}>
              <X size={28} />
            </button>
          </div>

          <div className={classes.modalBody} style={{ paddingTop: '10px' }}>
            {/* --- STEP 1 --- */}
            {currentStep === 1 && (
              <>
                <div className={classes.dashedBox}>
                  <div className={classes.iconGreenBox}><Download size={24} /></div>
                  <h4 style={{ margin: '10px 0 5px', fontSize: '16px', fontWeight: 600 }}>savings_data_template.xlsx</h4>
                  <p style={{ fontSize: '13px', color: '#4b5563', maxWidth: '400px', margin: '10px auto 0', lineHeight: 1.5 }}>
                    Download our ready-made template, fill in your data offline at your own pace, then re-upload.
                  </p>
                </div>
                <div className={classes.stepsList}>
                  <div className={classes.stepItem}><CheckCircle size={20} className={classes.greenIcon} /> Click Download Template</div>
                  <div className={classes.stepItem}><CheckCircle size={20} className={classes.greenIcon} /> Open the file and fill in your members' savings details</div>
                  <div className={classes.stepItem}><CheckCircle size={20} className={classes.greenIcon} /> Click Upload document and upload the completed file</div>
                </div>
                <div className={classes.modalFooter} style={{ borderTop: '1px solid #e5e7eb', marginTop: '20px', paddingTop: '16px' }}>
                  <div className={classes.modalBtnRow} style={{ width: '100%', justifyContent: 'space-between' }}>
                    <button type="button" className={classes.btnUploadOutline} onClick={() => setCurrentStep(2)}>
                      <Upload size={18} style={{ marginRight: '6px' }} /> Upload Document
                    </button>
                    <button type="button" className={classes.btnUploadSolid} onClick={handleDownloadTemplate} disabled={isDownloading}>
                      {isDownloading ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                           <Spinner animation="border" size="sm" /> Downloading...
                        </span>
                      ) : (
                        <><Download size={18} style={{ marginRight: '6px' }} /> Download Template</>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* --- STEP 2 --- */}
            {currentStep === 2 && (
              <>
                <div className={classes.successHeader}>
                  <CheckCircle size={22} className={classes.greenIcon} fill="#16A34A" color="white" />
                  <span>Template downloaded - now upload your completed file below</span>
                </div>
                <div className={classes.uploadDashedBox} onClick={() => fileInputRef.current?.click()} onDrop={handleDrop} onDragOver={handleDragOver}>
                  <div className={classes.uploadIconBox}><Upload size={24} /></div>
                  <h4 style={{ margin: '0', fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>Click to upload your completed file</h4>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0' }}>or drag and drop .xlsx, .xls accepted</p>
                  <input type="file" accept=".xlsx,.xls" hidden ref={fileInputRef} onChange={handleFileSelect} />
                  {file && <div style={{ marginTop: '10px', fontSize: '13px', fontWeight: 500, color: '#16A34A' }}>✓ {file.name}</div>}
                </div>

                {/* === FORM FIELDS (TIGHTENED SPACING) === */}
                <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  
                  {/* Row 1: Savings Type (Full Width) */}
                  <div className={classes.modalCol}>
                    <label>Savings Type</label>
                    <select className={classes.modalInput} value={savingsType} onChange={(e) => setSavingsType(e.target.value)}>
                      <option value="">Select savings type</option>
                      {loadingSavingsProducts ? (
                        <option>Loading savings...</option>
                      ) : savingsProducts.length > 0 ? (
                        savingsProducts.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.product_name || p.name || `Savings #${p.id}`}
                          </option>
                        ))
                      ) : (
                        <option>No savings types available</option>
                      )}
                    </select>
                  </div>

                  {/* Row 2: Currency (Full Width) */}
                  <div className={classes.modalCol}>
                    <label>Currency</label>
                    <select className={classes.modalInput} value={currency} onChange={(e) => setCurrency(e.target.value)}>
                      <option value="">Select Currency</option>
                      {loadingCurrencies ? (
                        <option>Loading...</option>
                      ) : currencies.length > 0 ? (
                        currencies.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.code} - {c.currency}
                          </option>
                        ))
                      ) : (
                        <option>No currencies available</option>
                      )}
                    </select>
                  </div>

                  {/* Row 3: Transaction Date (Full Width) */}
                  <div className={classes.modalCol}>
                    <label>Transaction Date</label>
                    <input type="date" className={classes.modalInput} value={date} onChange={(e) => setDate(e.target.value)} />
                  </div>
                </div>

                {/* === FOOTER (TIGHTENED SPACING) === */}
                <div className={classes.modalFooter} style={{ borderTop: '1px solid #e5e7eb', marginTop: '12px', paddingTop: '12px' }}>
                  <div className={classes.modalBtnRow} style={{ width: '100%', justifyContent: 'space-between' }}>
                    <button type="button" className={classes.btnUploadOutline} onClick={onClose}>Cancel</button>
                    <button type="button" className={classes.btnUploadSolid} onClick={handleSubmit} disabled={isSaving}>
                      {isSaving ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}><Spinner animation="border" size="sm" /> Saving...</span>
                      ) : ( "Save" )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadExcelModal2;